import { SWRConfiguration, Cache } from "swr";

const CACHE_KEY = "stellenscout-swr-cache";
const CACHE_VERSION = 1;
const MAX_CACHE_SIZE = 100; // Maximum number of entries

interface CacheEntry {
  data: unknown;
  timestamp: number;
}

interface StoredCache {
  version: number;
  entries: [string, CacheEntry][];
}

// localStorage cache provider for persistence across sessions
export function localStorageProvider(): Cache<unknown> {
  // Load from localStorage on init
  let map: Map<string, CacheEntry>;

  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (stored) {
      const parsed: StoredCache = JSON.parse(stored);
      // Check version and migrate if needed
      if (parsed.version === CACHE_VERSION) {
        map = new Map(parsed.entries);
      } else {
        // Version mismatch, start fresh
        map = new Map();
        localStorage.removeItem(CACHE_KEY);
      }
    } else {
      map = new Map();
    }
  } catch {
    map = new Map();
  }

  // Clean up old stellenscout polling keys (migration from old system)
  const oldKeys = [
    "stellenscout_polling_state",
    "stellenscout_current_job_id",
    "stellenscout_job_status",
    "stellenscout_job_result",
    "stellenscout_is_polling",
    "stellenscout_job_error",
    "stellenscout_current_step",
  ];
  oldKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  });

  // Persist to localStorage on page unload
  const saveCache = () => {
    try {
      // Limit cache size by removing oldest entries
      const entries = Array.from(map.entries());
      if (entries.length > MAX_CACHE_SIZE) {
        // Sort by timestamp and keep newest
        entries.sort((a, b) => (b[1].timestamp || 0) - (a[1].timestamp || 0));
        const trimmed = entries.slice(0, MAX_CACHE_SIZE);
        map = new Map(trimmed);
      }

      const toStore: StoredCache = {
        version: CACHE_VERSION,
        entries: Array.from(map.entries()),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
    } catch {
      // localStorage might be full or unavailable
    }
  };

  window.addEventListener("beforeunload", saveCache);

  // Also save periodically (every 30 seconds) to avoid data loss
  const intervalId = setInterval(saveCache, 30000);

  // Return a Map-like interface that SWR expects
  return {
    get(key: string) {
      const entry = map.get(key);
      return entry?.data;
    },
    set(key: string, value: unknown) {
      map.set(key, { data: value, timestamp: Date.now() });
    },
    delete(key: string) {
      map.delete(key);
    },
    keys() {
      return map.keys();
    },
  } as Cache<unknown>;
}

export const swrConfig: SWRConfiguration = {
  // Cache settings
  dedupingInterval: 2000, // Dedupe requests within 2s (was 60s - too long)
  revalidateOnFocus: true, // Refresh when user returns to tab
  revalidateOnReconnect: true, // Refresh when reconnecting
  revalidateIfStale: true, // Revalidate if data is stale

  // Error retry
  errorRetryCount: 3,
  errorRetryInterval: 1000,

  // Keep previous data while revalidating (prevents loading flicker)
  keepPreviousData: true,

  // Don't revalidate on mount if data exists (use cache)
  revalidateOnMount: true,
};
